import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Tabs, Tab, Paper, Typography, Button, Grid, TextField, Select, MenuItem, InputLabel, FormControl, FormHelperText, Divider, Snackbar, Alert, Chip, Stack, Card, CardContent, CardActionArea, CardMedia, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { fetchJson } from '../../../utils/fetchJson';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';
import { usePhase } from '../../../contexts/PhaseContext';
import { ID_CARD_PRESETS } from './idCardPresets';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return (
    <Box sx={{ pt: 2 }}>
      {children}
    </Box>
  );
}

// Simple wrappers to render QR and Barcode values into canvas/SVG
function QRCodeBox({ value, sx }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!wrapRef.current || !canvasRef.current || !value) return;
    let ro = null;
    try {
      const draw = () => {
        const rect = wrapRef.current.getBoundingClientRect();
        const w = Math.max(1, Math.floor(rect.width));
        const h = Math.max(1, Math.floor(rect.height));
        const dpr = window.devicePixelRatio || 1;
        const outW = Math.max(1, Math.floor(w * dpr));
        const outH = Math.max(1, Math.floor(h * dpr));

        canvasRef.current.width = outW;
        canvasRef.current.height = outH;
        canvasRef.current.style.width = `${w}px`;
        canvasRef.current.style.height = `${h}px`;

        const off = document.createElement('canvas');
        const square = Math.max(1, Math.floor(Math.min(outW, outH)));
        off.width = square;
        off.height = square;
        QRCode.toCanvas(off, String(value), { errorCorrectionLevel: 'M', margin: 0, width: square });

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) return;
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, outW, outH);
        const drawSize = square;
        const dx = Math.floor((outW - drawSize) / 2);
        const dy = Math.floor((outH - drawSize) / 2);
        ctx.drawImage(off, 0, 0, drawSize, drawSize, dx, dy, drawSize, drawSize);
      };
      draw();
      ro = new ResizeObserver(() => draw());
      ro.observe(wrapRef.current);
    } catch (e) { /* noop */ }
    return () => {
      try { ro && ro.disconnect(); } catch (_) {}
    };
  }, [value]);
  return (
    <Box ref={wrapRef} sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', ...sx }}>
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </Box>
  );
}

function BarcodeBox({ value, sx }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!wrapRef.current || !canvasRef.current || !value) return;
    let ro = null;
    try {
      const draw = () => {
        const rect = wrapRef.current.getBoundingClientRect();
        const w = Math.max(1, Math.floor(rect.width));
        const h = Math.max(1, Math.floor(rect.height));
        const dpr = window.devicePixelRatio || 1;
        const outW = Math.max(1, Math.floor(w * dpr));
        const outH = Math.max(1, Math.floor(h * dpr));

        canvasRef.current.width = outW;
        canvasRef.current.height = outH;
        canvasRef.current.style.width = `${w}px`;
        canvasRef.current.style.height = `${h}px`;

        const str = String(value);
        const off = document.createElement('canvas');
        JsBarcode(off, str, { format: 'CODE128', displayValue: false, margin: 0, width: Math.max(1, Math.min(4, Math.round(dpr * 2))), height: outH });

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) return;
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, outW, outH);
        const scale = Math.min(outW / Math.max(1, off.width), outH / Math.max(1, off.height));
        const drawW = Math.max(1, Math.floor(off.width * scale));
        const drawH = Math.max(1, Math.floor(off.height * scale));
        const dx = Math.floor((outW - drawW) / 2);
        const dy = Math.floor((outH - drawH) / 2);
        ctx.drawImage(off, 0, 0, off.width, off.height, dx, dy, drawW, drawH);
      };
      draw();
      ro = new ResizeObserver(() => draw());
      ro.observe(wrapRef.current);
    } catch (e) { /* noop */ }
    return () => {
      try { ro && ro.disconnect(); } catch (_) {}
    };
  }, [value]);
  return (
    <Box ref={wrapRef} sx={{ width: '100%', height: '100%', overflow: 'hidden', ...sx }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
    </Box>
  );
}

function IDCardWorkshopInner() {
  const { t } = useTranslation();
  const CUSTOM_PRESETS_STORAGE_KEY = 'ameca.idCard.customPresets.v1';
  const { phaseId } = usePhase();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [schoolBrand, setSchoolBrand] = useState({ name: '', primaryColor: '#003366', accentColor: '#FFD700', logoUrl: '' });
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [designerJSON, setDesignerJSON] = useState({});

  const [filters, setFilters] = useState({ role: 'Student', classId: '', status: 'active', q: '' });
  const [recipients, setRecipients] = useState([]);
  // Whether a saved design is pinned to the phase being worked in, or available
  // to every phase in the school. Never an academic year — see the note where
  // the payload is built.
  const [phaseOnly, setPhaseOnly] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [printOptions, setPrintOptions] = useState({ pageSize: 'A4', rows: 4, cols: 2, gapMm: 5, marginMm: 10 });
  const [cardPrefix, setCardPrefix] = useState('');
  const [nextSuggestion, setNextSuggestion] = useState('');
  const [quickPreviewOpen, setQuickPreviewOpen] = useState(false);
  const [quickPreviewData, setQuickPreviewData] = useState(null);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [previewCards, setPreviewCards] = useState([]);
  const [classStudents, setClassStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);

  const [issueQueue, setIssueQueue] = useState([]);
  const [history, setHistory] = useState({ rows: [], count: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [clipboardEl, setClipboardEl] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [selectionBox, setSelectionBox] = useState({ active: false, x1: 0, y1: 0, x2: 0, y2: 0 });

  const [customPresets, setCustomPresets] = useState([]);
  const didInitialTemplateAutoselectRef = useRef(false);

  const previewCardRefs = useRef([]);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [historyTick, setHistoryTick] = useState(0);
  const pendingUndoSnapshotRef = useRef(null);

  // No-code designer state
  const canvasRef = useRef(null);
  const [selectedElId, setSelectedElId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [dragState, setDragState] = useState({ mode: 'none', startX: 0, startY: 0, elStart: null });

  const cloneDesign = useCallback((v) => {
    try {
      return JSON.parse(JSON.stringify(v || {}));
    } catch (_) {
      return v ? { ...v } : {};
    }
  }, []);

  const pushUndo = useCallback((snapshot) => {
    try {
      undoStackRef.current.push(cloneDesign(snapshot));
      redoStackRef.current = [];
      setHistoryTick((t) => t + 1);
    } catch (_) {}
  }, [cloneDesign]);

  const handleUndo = useCallback(() => {
    try {
      if (!undoStackRef.current.length) return;
      const prev = undoStackRef.current.pop();
      redoStackRef.current.push(cloneDesign(designerJSON));
      setDesignerJSON(prev || {});
      setSelectedElId(null);
      setSelectedIds([]);
      setHistoryTick((t) => t + 1);
    } catch (_) {}
  }, [designerJSON, cloneDesign]);

  const canUndo = useMemo(() => undoStackRef.current.length > 0, [historyTick]);

  const selectedElement = useMemo(() => (designerJSON?.elements || []).find(el => el.id === selectedElId) || null, [designerJSON, selectedElId]);
  const elementsById = useMemo(() => Object.fromEntries(((designerJSON?.elements) || []).map(el => [el.id, el])), [designerJSON]);
  const selectedElements = useMemo(() => selectedIds.map(id => elementsById[id]).filter(Boolean), [selectedIds, elementsById]);

  const getCanvasRect = () => canvasRef.current?.getBoundingClientRect();
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const updateElement = (id, updater) => {
    setDesignerJSON(prev => {
      const prevEls = prev?.elements || [];
      const nextEls = prevEls.map(el => el.id === id ? { ...el, ...updater(el) } : el);
      return { ...prev, elements: nextEls };
    });
  };

  const updateSelection = (updaterOrPartial) => {
    const ids = selectedIds.length ? selectedIds : (selectedElId ? [selectedElId] : []);
    if (!ids.length) return;
    setDesignerJSON(prev => {
      pushUndo(prev);
      const prevEls = prev?.elements || [];
      const nextEls = prevEls.map(el => {
        if (!ids.includes(el.id)) return el;
        const patch = typeof updaterOrPartial === 'function' ? updaterOrPartial(el) : updaterOrPartial;
        return { ...el, ...patch };
      });
      return { ...prev, elements: nextEls };
    });
  };

  const onResizeHandleMouseDown = (e) => {
    e.stopPropagation();
    const rect = getCanvasRect();
    if (!rect) return;
    pendingUndoSnapshotRef.current = cloneDesign(designerJSON);
    const ids = selectedIds.length ? selectedIds : (selectedElId ? [selectedElId] : []);
    if (!ids.length) return;
    const primaryId = selectedElId || ids[0];
    const prim = elementsById[primaryId];
    const aspectRatio = prim?.w && prim?.h ? (prim.w / prim.h) : 1;
    const lockAspect = !!(prim?.lockAspect || prim?.shapeType === 'circle');
    const groupStarts = {};
    ids.forEach(id => {
      const it = elementsById[id];
      if (it) groupStarts[id] = { x: it.x, y: it.y, w: it.w, h: it.h };
    });
    const elStart = prim ? { x: prim.x, y: prim.y, w: prim.w, h: prim.h, aspectRatio, lockAspect } : { x: 0, y: 0, w: 0, h: 0, aspectRatio, lockAspect };
    setDragState({ mode: 'resize', startX: e.clientX, startY: e.clientY, elStart, groupStarts });
  };
  const replaceElement = (id, next) => {
    setDesignerJSON(prev => {
      pushUndo(prev);
      const prevEls = prev?.elements || [];
      const nextEls = prevEls.map(el => el.id === id ? next : el);
      return { ...prev, elements: nextEls };
    });
  };
  const toAbsoluteUrl = (url) => {
    const configuredBaseRaw = (
      process.env.REACT_APP_API_BASE_URL ||
      process.env.VITE_API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      ''
    ).trim();
    const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalBrowserHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(browserHost);
    const isLocalNodeEnv = process.env.NODE_ENV === 'development';
    const base = (configuredBaseRaw || ((isLocalBrowserHost || isLocalNodeEnv) ? 'http://localhost:5000' : '')).replace(/\/$/, '');
    if (!url) return url;
    if (/^https?:\/\//i.test(url)) return url;
    if (/^(data|blob):/i.test(url)) return url;
    return `${base}${url.startsWith('/') ? url : `/${url}`}`;
  };
  const resolveImageSrc = useCallback((src) => {
    const placeholderLogo = '/placeholderImg.png';
    const placeholderPhoto = '/placeholderImg.png';
    if (!src || typeof src !== 'string') return placeholderPhoto;
    const binding = (previewData?.sampleBinding) || {};
    if (src.startsWith('{') && src.endsWith('}')) {
      if (src === '{school.logoUrl}') return toAbsoluteUrl(schoolBrand.logoUrl || binding['{school.logoUrl}'] || placeholderLogo);
      if (src === '{student.photoUrl}') return toAbsoluteUrl(binding['{student.photoUrl}'] || placeholderPhoto);
      if (src === '{user.avatarUrl}') return toAbsoluteUrl(binding['{user.avatarUrl}'] || placeholderPhoto);
      if (src === '{staff.photoUrl}') return toAbsoluteUrl(binding['{staff.photoUrl}'] || binding['{user.avatarUrl}'] || placeholderPhoto);
      return toAbsoluteUrl(binding[src] || placeholderPhoto);
    }
    return toAbsoluteUrl(src);
  }, [previewData, schoolBrand]);

  useEffect(() => {
    const loadSchoolBrand = async () => {
      try {
        const res = await fetchJson('/api/schools/me');
        const school = res?.school;
        if (!school) return;
        setSchoolBrand(prev => ({
          ...prev,
          name: school.name || prev.name,
          logoUrl: school.logoUrl ? toAbsoluteUrl(school.logoUrl) : prev.logoUrl
        }));
      } catch (_) {
      }
    };
    loadSchoolBrand();
  }, []);
  const addElement = (type) => {
    const id = `el-${Date.now()}-${Math.floor(Math.random()*1000)}`;
    const base = { id, x: 40, y: 30, w: 20, h: 12 };
    let el = null;
    if (type === 'text') el = { ...base, type: 'text', text: '{name}', fontSize: 7, fontWeight: 700, color: '#222222' };
    if (type === 'shape') el = { ...base, type: 'shape', color: designerJSON?.background?.accentColor || '#1976D2', radius: 0, opacity: 1, borderColor: '#00000000', borderWidth: 0, shapeType: 'rect', lockAspect: false };
    if (type === 'line') {
      el = { ...base, type: 'line', color: '#111827', thicknessPx: 2, rotate: 0, w: 40, h: 0 }; // h ignored
    }
    if (type === 'photo') {
      const isStudent = (filters.role || '').toLowerCase() === 'student';
      el = { ...base, type: 'photo', radius: 6, imgBinding: isStudent ? 'student' : 'user', src: isStudent ? '{student.photoUrl}' : '{user.avatarUrl}' };
    }
    if (type === 'logo') el = { ...base, type: 'logo', w: 14, h: 14, imgBinding: 'school', src: '{school.logoUrl}' };
    if (type === 'qr') el = { ...base, type: 'qr', w: 14, h: 20, data: '{qr.fullInfo}' };
    if (type === 'barcode') el = { ...base, type: 'barcode', w: 40, h: 14, data: '{studentNumber}' };
    if (!el) return;
    setDesignerJSON(prev => {
      pushUndo(prev);
      return { ...prev, elements: [ ...(prev?.elements || []), el ] };
    });
    setSelectedElId(id);
    setSelectedIds([id]);
  };
  const deleteElement = (id) => {
    setDesignerJSON(prev => {
      pushUndo(prev);
      return { ...prev, elements: (prev?.elements || []).filter(el => el.id !== id) };
    });
    if (selectedElId === id) setSelectedElId(null);
    setSelectedIds(prev => prev.filter(x => x !== id));
  };

  const copySelectedElement = useCallback(() => {
    if (!selectedElement) return;
    setClipboardEl({ ...selectedElement });
    setSuccess(t('idCard.elementCopied'));
  }, [selectedElement]);

  const pasteElement = useCallback(() => {
    if (!clipboardEl) return;
    const id = `el-${Date.now()}-${Math.floor(Math.random()*1000)}`;
    const offset = 3; // percent offset to reveal paste
    const base = clipboardEl;
    const newX = clamp((base.x || 0) + offset, 0, 100 - (base.w || 10));
    const newY = clamp((base.y || 0) + offset, 0, 100 - (base.h || 10));
    const newEl = { ...base, id, x: newX, y: newY };
    setDesignerJSON(prev => {
      pushUndo(prev);
      return { ...prev, elements: [ ...(prev?.elements || []), newEl ] };
    });
    setSelectedElId(id);
    setSuccess(t('idCard.elementPasted'));
  }, [clipboardEl, pushUndo]);

  const duplicateSelectedElement = useCallback(() => {
    if (!selectedElement) return;
    const base = selectedElement;
    const id = `el-${Date.now()}-${Math.floor(Math.random()*1000)}`;
    const offset = 3;
    const newX = clamp((base.x || 0) + offset, 0, 100 - (base.w || 10));
    const newY = clamp((base.y || 0) + offset, 0, 100 - (base.h || 10));
    const newEl = { ...base, id, x: newX, y: newY };
    setDesignerJSON(prev => {
      pushUndo(prev);
      return { ...prev, elements: [ ...(prev?.elements || []), newEl ] };
    });
    setSelectedElId(id);
    setSuccess(t('idCard.elementDuplicated'));
  }, [selectedElement, pushUndo]);

  const alignSelectedElements = useCallback((mode) => {
    if (!selectedIds || selectedIds.length < 2) return;
    const all = (designerJSON?.elements || []);
    const sels = selectedIds.map(id => all.find(e => e.id === id)).filter(Boolean);
    if (sels.length < 2) return;
    const minX = Math.min(...sels.map(e => e.x));
    const minY = Math.min(...sels.map(e => e.y));
    const maxX = Math.max(...sels.map(e => e.x + e.w));
    const maxY = Math.max(...sels.map(e => e.y + e.h));
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    setDesignerJSON(prev => {
      pushUndo(prev);
      const prevEls = prev?.elements || [];
      const nextEls = prevEls.map(el => {
        if (!selectedIds.includes(el.id)) return el;
        let nx = el.x;
        let ny = el.y;
        if (mode === 'left') nx = minX;
        if (mode === 'centerX') nx = centerX - el.w / 2;
        if (mode === 'right') nx = maxX - el.w;
        if (mode === 'top') ny = minY;
        if (mode === 'middle') ny = centerY - el.h / 2;
        if (mode === 'bottom') ny = maxY - el.h;
        return { ...el, x: clamp(nx, 0, 100 - el.w), y: clamp(ny, 0, 100 - el.h) };
      });
      return { ...prev, elements: nextEls };
    });
    setSuccess(t('idCard.alignedSelection'));
  }, [selectedIds, designerJSON, pushUndo]);

  const distributeSelectedElements = useCallback((orientation) => {
    if (!selectedIds || selectedIds.length < 3) return;
    const all = (designerJSON?.elements || []);
    const sels = selectedIds.map(id => all.find(e => e.id === id)).filter(Boolean);
    if (sels.length < 3) return;
    if (orientation === 'h') {
      const sorted = [...sels].sort((a, b) => a.x - b.x);
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const span = (last.x + last.w) - first.x;
      const totalWidth = sorted.reduce((sum, e) => sum + e.w, 0);
      const gaps = sorted.length - 1;
      const spacing = (span - totalWidth) / gaps;
      let cursor = first.x + first.w + spacing;
      const positions = new Map();
      for (let i = 1; i < sorted.length - 1; i++) {
        positions.set(sorted[i].id, cursor);
        cursor += sorted[i].w + spacing;
      }
      setDesignerJSON(prev => {
        pushUndo(prev);
        const prevEls = prev?.elements || [];
        const nextEls = prevEls.map(el => {
          if (!positions.has(el.id)) return el;
          const nx = positions.get(el.id);
          return { ...el, x: clamp(nx, 0, 100 - el.w) };
        });
        return { ...prev, elements: nextEls };
      });
    } else if (orientation === 'v') {
      const sorted = [...sels].sort((a, b) => a.y - b.y);
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const span = (last.y + last.h) - first.y;
      const totalHeight = sorted.reduce((sum, e) => sum + e.h, 0);
      const gaps = sorted.length - 1;
      const spacing = (span - totalHeight) / gaps;
      let cursor = first.y + first.h + spacing;
      const positions = new Map();
      for (let i = 1; i < sorted.length - 1; i++) {
        positions.set(sorted[i].id, cursor);
        cursor += sorted[i].h + spacing;
      }
      setDesignerJSON(prev => {
        pushUndo(prev);
        const prevEls = prev?.elements || [];
        const nextEls = prevEls.map(el => {
          if (!positions.has(el.id)) return el;
          const ny = positions.get(el.id);
          return { ...el, y: clamp(ny, 0, 100 - el.h) };
        });
        return { ...prev, elements: nextEls };
      });
    }
    setSuccess(t('idCard.distributedSelection'));
  }, [selectedIds, designerJSON, pushUndo]);

  const groupSelectedElements = useCallback(() => {
    if (!selectedIds || selectedIds.length < 2) return;
    const groupId = `grp-${Date.now()}-${Math.floor(Math.random()*1000)}`;
    setDesignerJSON(prev => {
      pushUndo(prev);
      const prevEls = prev?.elements || [];
      const nextEls = prevEls.map(el => selectedIds.includes(el.id) ? { ...el, groupId } : el);
      return { ...prev, elements: nextEls };
    });
    setSuccess(t('idCard.groupedSelection'));
  }, [selectedIds, pushUndo]);

  const ungroupSelectedElements = useCallback(() => {
    if (!selectedIds || selectedIds.length === 0) return;
    setDesignerJSON(prev => {
      pushUndo(prev);
      const prevEls = prev?.elements || [];
      const nextEls = prevEls.map(el => selectedIds.includes(el.id) ? (() => { const { groupId, ...rest } = el; return rest; })() : el);
      return { ...prev, elements: nextEls };
    });
    setSuccess(t('idCard.ungroupedSelection'));
  }, [selectedIds, pushUndo]);

  const onElementMouseDown = (e, el, mode = 'drag') => {
    e.stopPropagation();
    const rect = getCanvasRect();
    if (!rect) return;
    if (mode === 'drag') pendingUndoSnapshotRef.current = cloneDesign(designerJSON);
    const toggle = e.shiftKey || e.ctrlKey || e.metaKey;
    const groupIds = el.groupId ? (designerJSON?.elements || []).filter(x => x.groupId === el.groupId).map(x => x.id) : [el.id];
    if (toggle) {
      setSelectedIds(prev => {
        const allIn = groupIds.every(id => prev.includes(id));
        if (allIn) return prev.filter(id => !groupIds.includes(id));
        return Array.from(new Set([...prev, ...groupIds]));
      });
      setSelectedElId(el.id);
    } else {
      setSelectedIds(groupIds);
      setSelectedElId(el.id);
    }
    const aspectRatio = el.w && el.h ? (el.w / el.h) : 1;
    const lockAspect = !!(el.lockAspect || el.shapeType === 'circle');
    const groupStarts = {};
    ((el.groupId ? (designerJSON?.elements || []).filter(x => x.groupId === el.groupId).map(x => x.id) : (selectedIds.length ? selectedIds : [el.id]))).forEach(id => {
      const it = elementsById[id] || el;
      groupStarts[id] = { x: it.x, y: it.y, w: it.w, h: it.h };
    });
    setDragState({ mode, startX: e.clientX, startY: e.clientY, elStart: { x: el.x, y: el.y, w: el.w, h: el.h, aspectRatio, lockAspect }, groupStarts });
  };

  useEffect(() => {
    const onMove = (e) => {
      if (dragState.mode === 'none' || !dragState.elStart) return;
      const rect = getCanvasRect();
      if (!rect) return;
      const dxPct = ((e.clientX - dragState.startX) / rect.width) * 100;
      const dyPct = ((e.clientY - dragState.startY) / rect.height) * 100;
      const start = dragState.elStart;
      if ((dragState.mode === 'drag' || dragState.mode === 'resize') && pendingUndoSnapshotRef.current) {
        pushUndo(pendingUndoSnapshotRef.current);
        pendingUndoSnapshotRef.current = null;
      }
      if (dragState.mode === 'drag' && (selectedIds.length || selectedElId)) {
        const ids = selectedIds.length ? selectedIds : [selectedElId];
        // snapping and guidelines
        const primaryId = selectedElId || ids[0];
        const sPrim = (dragState.groupStarts && dragState.groupStarts[primaryId]) || start;
        let targetX = clamp(sPrim.x + dxPct, 0, 100 - sPrim.w);
        let targetY = clamp(sPrim.y + dyPct, 0, 100 - sPrim.h);
        const tolerance = 0.6;
        const guides = [];
        const others = (designerJSON?.elements || []).filter(e2 => !ids.includes(e2.id));
        const primLeft = targetX;
        const primRight = targetX + sPrim.w;
        const primVCenter = targetX + sPrim.w / 2;
        const primTop = targetY;
        const primBottom = targetY + sPrim.h;
        const primHCenter = targetY + sPrim.h / 2;
        if (Math.abs(primVCenter - 50) <= tolerance) { targetX = 50 - sPrim.w / 2; guides.push({ axis: 'v', pos: 50, label: 'Center' }); }
        if (Math.abs(primHCenter - 50) <= tolerance) { targetY = 50 - sPrim.h / 2; guides.push({ axis: 'h', pos: 50, label: 'Center' }); }
        others.forEach(o => {
          const oLeft = o.x; const oRight = o.x + o.w; const oVCenter = o.x + o.w / 2;
          const oTop = o.y; const oBottom = o.y + o.h; const oHCenter = o.y + o.h / 2;
          const testsX = [
            { cur: primLeft, ref: oLeft, label: 'Left', snap: () => { targetX = oLeft; guides.push({ axis: 'v', pos: oLeft, label: 'Align' }); } },
            { cur: primLeft, ref: oRight, label: 'Left→Right', snap: () => { targetX = oRight; guides.push({ axis: 'v', pos: oRight, label: 'Align' }); } },
            { cur: primRight, ref: oRight, label: 'Right', snap: () => { targetX = oRight - sPrim.w; guides.push({ axis: 'v', pos: oRight, label: 'Align' }); } },
            { cur: primRight, ref: oLeft, label: 'Right→Left', snap: () => { targetX = oLeft - sPrim.w; guides.push({ axis: 'v', pos: oLeft, label: 'Align' }); } },
            { cur: primVCenter, ref: oVCenter, label: 'Center', snap: () => { targetX = oVCenter - sPrim.w / 2; guides.push({ axis: 'v', pos: oVCenter, label: 'Center' }); } },
          ];
          for (const t of testsX) { if (Math.abs(t.cur - t.ref) <= tolerance) { t.snap(); break; } }
          const testsY = [
            { cur: primTop, ref: oTop, label: 'Top', snap: () => { targetY = oTop; guides.push({ axis: 'h', pos: oTop, label: 'Align' }); } },
            { cur: primTop, ref: oBottom, label: 'Top→Bottom', snap: () => { targetY = oBottom; guides.push({ axis: 'h', pos: oBottom, label: 'Align' }); } },
            { cur: primBottom, ref: oBottom, label: 'Bottom', snap: () => { targetY = oBottom - sPrim.h; guides.push({ axis: 'h', pos: oBottom, label: 'Align' }); } },
            { cur: primBottom, ref: oTop, label: 'Bottom→Top', snap: () => { targetY = oTop - sPrim.h; guides.push({ axis: 'h', pos: oTop, label: 'Align' }); } },
            { cur: primHCenter, ref: oHCenter, label: 'Center', snap: () => { targetY = oHCenter - sPrim.h / 2; guides.push({ axis: 'h', pos: oHCenter, label: 'Center' }); } },
          ];
          for (const t of testsY) { if (Math.abs(t.cur - t.ref) <= tolerance) { t.snap(); break; } }
        });
        const deltaX = targetX - sPrim.x;
        const deltaY = targetY - sPrim.y;
        ids.forEach(id => {
          const s = (dragState.groupStarts && dragState.groupStarts[id]) || start;
          const newX = clamp(s.x + deltaX, 0, 100 - s.w);
          const newY = clamp(s.y + deltaY, 0, 100 - s.h);
          updateElement(id, () => ({ x: newX, y: newY }));
        });
        // store guides for render (thin lines, non-blocking)
        // de-dupe guides by axis/pos
        const key = g => `${g.axis}:${Math.round(g.pos*1000)/1000}`;
        const uniq = [];
        const seen = new Set();
        for (const g of guides) { const k = key(g); if (!seen.has(k)) { seen.add(k); uniq.push(g); } }
        window.__smartGuides = uniq;
        // distance label from canvas top-left for primary element
        const leftPx = Math.round((targetX / 100) * rect.width);
        const topPx = Math.round((targetY / 100) * rect.height);
        window.__smartLabelLT = { xPct: targetX, yPct: targetY, text: `${leftPx}px, ${topPx}px` };
      }
      if (dragState.mode === 'marquee') {
        const rect = getCanvasRect();
        const x1 = clamp(((dragState.startX - rect.left) / rect.width) * 100, 0, 100);
        const y1 = clamp(((dragState.startY - rect.top) / rect.height) * 100, 0, 100);
        const x2 = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
        const y2 = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);
        setSelectionBox({ active: true, x1, y1, x2, y2 });
        const left = Math.min(x1, x2);
        const top = Math.min(y1, y2);
        const right = Math.max(x1, x2);
        const bottom = Math.max(y1, y2);
        const inside = (designerJSON?.elements || []).filter(el => el.x >= left && el.y >= top && (el.x + el.w) <= right && (el.y + el.h) <= bottom).map(el => el.id);
        setSelectedIds(inside);
      }
      if (dragState.mode === 'resize' && (selectedIds.length || selectedElId)) {
        const ids = selectedIds.length ? selectedIds : [selectedElId];
        // Build initial selection bounding box from groupStarts
        const starts = ids.map(id => dragState.groupStarts && dragState.groupStarts[id]).filter(Boolean);
        const minX = Math.min(...starts.map(s => s.x));
        const minY = Math.min(...starts.map(s => s.y));
        const maxX = Math.max(...starts.map(s => s.x + s.w));
        const maxY = Math.max(...starts.map(s => s.y + s.h));
        const selStartW = maxX - minX;
        const selStartH = maxY - minY;
        // Fallback for single element if something is missing
        const baseStart = starts[0] || start;
        const startW = selStartW || baseStart.w;
        const startH = selStartH || baseStart.h;
        // Target selection size
        let targetW = clamp(startW + dxPct, 5, 100 - minX);
        let targetH = clamp(startH + dyPct, 5, 100 - minY);
        // If single element with locked aspect, preserve it
        if (ids.length === 1 && start.lockAspect) {
          const ratio = start.aspectRatio || 1;
          const propW = Math.abs(targetW - startW);
          const propH = Math.abs(targetH - startH);
          if (propW >= propH) {
            targetH = clamp(targetW / ratio, 5, 100 - minY);
          } else {
            targetW = clamp(targetH * ratio, 5, 100 - minX);
          }
        }
        const scaleX = startW > 0 ? (targetW / startW) : 1;
        const scaleY = startH > 0 ? (targetH / startH) : 1;
        ids.forEach(id => {
          const s = (dragState.groupStarts && dragState.groupStarts[id]) || start;
          // Scale position relative to selection top-left
          const relX = s.x - minX;
          const relY = s.y - minY;
          let newW = Math.max(1, s.w * scaleX);
          let newH = Math.max(1, s.h * scaleY);
          let newX = minX + relX * scaleX;
          let newY = minY + relY * scaleY;
          // Clamp to canvas bounds
          newW = clamp(newW, 1, 100 - newX);
          newH = clamp(newH, 1, 100 - newY);
          updateElement(id, () => ({ x: newX, y: newY, w: newW, h: newH }));
        });
      }
    };
    const onUp = () => { pendingUndoSnapshotRef.current = null; setDragState({ mode: 'none', startX: 0, startY: 0, elStart: null }); setSelectionBox(prev => ({ ...prev, active: false })); window.__smartGuides = []; window.__smartLabelLT = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    const onKey = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC')>=0;
      const meta = isMac ? e.metaKey : e.ctrlKey;
      if (!meta) return;
      if (e.key.toLowerCase() === 'z') {
        e.preventDefault();
        handleUndo();
      }
      if (e.key.toLowerCase() === 'c') {
        e.preventDefault();
        copySelectedElement();
      }
      if (e.key.toLowerCase() === 'v') {
        e.preventDefault();
        pasteElement();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, [dragState, selectedElId, copySelectedElement, pasteElement, pushUndo, handleUndo]);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchJson('/api/id-cards/templates');
      // Also load classes for student scope
      try {
        const cls = await fetchJson('/api/classes');
        const list = Array.isArray(cls) ? cls : (cls?.rows || cls?.data || []);
        setClasses(list);
      } catch (_) {}
      setTemplates(data || []);
    } catch (e) {
      setError(e?.message || t('idCard.errors.loadTemplates'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CUSTOM_PRESETS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) setCustomPresets(parsed);
    } catch (_) {}
  }, []);

  const upsertCustomPreset = useCallback((preset) => {
    setCustomPresets((prev) => {
      const next = [preset, ...(prev || []).filter(p => String(p?.id) !== String(preset?.id))];
      try { localStorage.setItem(CUSTOM_PRESETS_STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  const allPresets = useMemo(() => {
    const builtins = Array.isArray(ID_CARD_PRESETS) ? ID_CARD_PRESETS : [];
    const customs = Array.isArray(customPresets) ? customPresets : [];
    return [...customs, ...builtins];
  }, [customPresets]);
  const defaultTextBinding = useMemo(() => ({
    '{school.name}': schoolBrand.name || t('idCard.placeholders.schoolName') || 'School Name',
    '{school.motto}': t('idCard.placeholders.schoolMotto') || 'School Motto',
    '{name}': t('idCard.placeholders.fullName') || 'Full Name',
    '{role}': filters.role || t('idCard.placeholders.role') || 'Role',
    '{studentId}': 'STU-001',
    '{studentNumber}': 'STU-001',
    '{className}': 'Class A',
    '{expiryDate}': '2026-12',
    '{student.dateOfBirth}': '2008-07-15',
    '{student.placeOfBirth}': 'Yaoundé',
    '{student.sex}': 'Male',
    '{student.address}': '123 Street',
    '{guardian.name}': t('idCard.placeholders.guardianName') || 'Guardian Name',
    '{guardian.phone}': '6XX-XXX-XXX',
    '{qr.fullInfo}': 'NAME:Full Name|ID:STU-001|CLASS:Class A|SCHOOL:School Name|ROLE:Student',
    '{qr.loginUrl}': 'STU-001',
    '{qr.profileUrl}': 'STU-001',
  }), [schoolBrand.name, filters.role, t]);

  const buildCardsPdf = useCallback(async () => {
    const format = String(printOptions.pageSize || 'A4').toLowerCase() === 'letter' ? 'letter' : 'a4';
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format });

    const waitForImages = async (root) => {
      try {
        const imgs = Array.from(root.querySelectorAll('img'));
        const bgUrls = (() => {
          try {
            const nodes = [root, ...Array.from(root.querySelectorAll('*'))];
            const urls = [];
            nodes.forEach((node) => {
              try {
                const bg = window.getComputedStyle(node).backgroundImage;
                if (!bg || bg === 'none') return;
                const re = /url\((?:"|')?(.*?)(?:"|')?\)/g;
                let m;
                while ((m = re.exec(bg)) !== null) {
                  if (m[1]) urls.push(m[1]);
                }
              } catch (_) {}
            });
            return urls;
          } catch (_) {
            return [];
          }
        })();

        if (imgs.length === 0 && bgUrls.length === 0) return;

        await Promise.all(imgs.map(img => new Promise((resolve) => {
          try {
            if (img.complete && img.naturalWidth > 0) return resolve();
            const onDone = () => {
              try { img.removeEventListener('load', onDone); } catch (_) {}
              try { img.removeEventListener('error', onDone); } catch (_) {}
              resolve();
            };
            img.addEventListener('load', onDone);
            img.addEventListener('error', onDone);
          } catch (_) {
            resolve();
          }
        })));

        await Promise.all(bgUrls.map((u) => new Promise((resolve) => {
          try {
            const img = new Image();
            try { img.crossOrigin = 'anonymous'; } catch (_) {}
            const onDone = () => resolve();
            img.onload = onDone;
            img.onerror = onDone;
            img.src = u;
            if (img.complete && img.naturalWidth > 0) resolve();
          } catch (_) {
            resolve();
          }
        })));
      } catch (_) {
      }
    };

    const rows = Math.max(1, Number(printOptions.rows) || 5);
    const cols = Math.max(1, Number(printOptions.cols) || 2);
    const gap = Math.max(0, Number(printOptions.gapMm) || 5);
    const margin = Math.max(0, Number(printOptions.marginMm) || 10);

    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const availW = Math.max(1, pageW - margin * 2);
    const availH = Math.max(1, pageH - margin * 2);
    const cellW = (availW - gap * (cols - 1)) / cols;
    const cellH = (availH - gap * (rows - 1)) / rows;

    const nodes = (previewCardRefs.current || []).filter(Boolean);
    for (let i = 0; i < nodes.length; i += 1) {
      if (i > 0 && i % (rows * cols) === 0) pdf.addPage();
      const idxOnPage = i % (rows * cols);
      const r = Math.floor(idxOnPage / cols);
      const c = idxOnPage % cols;

      const cellX = margin + c * (cellW + gap);
      const cellY = margin + r * (cellH + gap);

      await waitForImages(nodes[i]);
      const canvas = await html2canvas(nodes[i], {
        scale: 3,
        useCORS: true,
        backgroundColor: null,
      });
      const ar = canvas.width > 0 && canvas.height > 0 ? (canvas.width / canvas.height) : 1.586;
      let drawW = cellW;
      let drawH = drawW / ar;
      if (drawH > cellH) {
        drawH = cellH;
        drawW = drawH * ar;
      }
      const x = cellX + (cellW - drawW) / 2;
      const y = cellY + (cellH - drawH) / 2;
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', x, y, drawW, drawH);
    }

    return pdf;
  }, [printOptions]);

  const handleDownloadPdf = useCallback(async () => {
    if (!previewData) return;
    try {
      setLoading(true);
      const pdf = await buildCardsPdf();
      pdf.save('id-cards.pdf');
    } catch (e) {
      setError(e?.message || t('idCard.errors.downloadPdf'));
    } finally {
      setLoading(false);
    }
  }, [previewData, buildCardsPdf]);

  const handlePrintCards = useCallback(async () => {
    if (!previewData) return;
    try {
      setLoading(true);
      const pdf = await buildCardsPdf();
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (_) {}
        setLoading(false);
        setTimeout(() => {
          try { URL.revokeObjectURL(url); } catch (_) {}
          try { document.body.removeChild(iframe); } catch (_) {}
        }, 2000);
      };
      document.body.appendChild(iframe);
    } catch (e) {
      setError(e?.message || t('idCard.errors.print'));
      setLoading(false);
    }
  }, [previewData, buildCardsPdf]);

  const renderTextContent = useCallback((text) => {
    if (!text) return '';
    const binding = previewData?.sampleBinding || {};
    return String(text).replace(/\{[^}]+\}/g, (tok) => {
      if (binding[tok] !== undefined && binding[tok] !== null) return String(binding[tok]);
      if (defaultTextBinding[tok] !== undefined) return String(defaultTextBinding[tok]);
      return tok; // leave token visible if unknown
    });
  }, [previewData, defaultTextBinding]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useScopeAutoRefresh([loadTemplates]);

  useEffect(() => {
    if (didInitialTemplateAutoselectRef.current) return;
    if (selectedTemplateId) { didInitialTemplateAutoselectRef.current = true; return; }
    if (!templates || templates.length === 0) return;
    const hasName = (templateName || '').trim().length > 0;
    const hasDesign = !!(designerJSON && Object.keys(designerJSON).length);
    if (hasName || hasDesign) return;
    didInitialTemplateAutoselectRef.current = true;
    setSelectedTemplateId(String(templates[0].id));
  }, [templates, selectedTemplateId, templateName, designerJSON]);

  // Load students when Student role and a class is selected
  useEffect(() => {
    const loadStudents = async () => {
      const isStudentRole = String(filters.role).toLowerCase() === 'student';
      if (!isStudentRole || !filters.classId) { setClassStudents([]); setSelectedStudentIds([]); return; }
      try {
        const res = await fetchJson(`/api/students?classId=${encodeURIComponent(filters.classId)}`);
        const rows = Array.isArray(res?.rows) ? res.rows : (Array.isArray(res) ? res : (res?.students || []));
        setClassStudents(rows);
      } catch (e) {
        setClassStudents([]);
      }
    };
    loadStudents();
  }, [filters.role, filters.classId]);

  // Keep recipients in sync with student selections
  useEffect(() => {
    const isStudentRole = String(filters.role).toLowerCase() === 'student';
    if (!isStudentRole) return;
    const idSet = new Set(selectedStudentIds);
    const mapped = classStudents
      .filter(s => idSet.has(s.id))
      .map(s => {
        const maybeUserId = s.userId || s.User?.id || null;
        return maybeUserId ? { studentId: s.id, userId: maybeUserId } : { studentId: s.id };
      });
    setRecipients(mapped);
  }, [selectedStudentIds, classStudents, filters.role]);

  const selectedTemplate = useMemo(() => templates.find(tmpl => String(tmpl.id) === String(selectedTemplateId)) || null, [templates, selectedTemplateId]);

  const validRecipients = useMemo(() => {
    const arr = Array.isArray(recipients) ? recipients : [];
    const out = [];
    const seen = new Set();
    for (const r of arr) {
      const uid = r?.userId;
      if (!uid) continue;
      const key = String(uid);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
    return out;
  }, [recipients]);

  useEffect(() => {
    // Sync name and canvas when switching selected template
    // Only clear templateName if we're actually selecting a saved template
    if (selectedTemplate) {
      setTemplateName(selectedTemplate.name || '');
      if (selectedTemplate.designJSON) {
        setDesignerJSON(selectedTemplate.designJSON);
      }
    }
    // Don't clear templateName when selectedTemplate becomes null (like when selecting a preset)
    // The checkbox has to describe the template that is open, or a save would
    // silently re-scope it.
    setPhaseOnly(Boolean(selectedTemplate?.phaseId));
  }, [selectedTemplate]);

  const handleCreateTemplate = async () => {
    try {
      setLoading(true);
      
      const trimmed = (templateName || '').trim();
      const fallbackName = `Template ${(templates?.length || 0) + 1}`;
      const finalName = trimmed || fallbackName;
      
      if (!finalName || finalName.trim() === '') {
        setError(t('idCard.errors.templateNameRequired'));
        return;
      }
      
      const payload = { 
        name: finalName, 
        roleScope: filters.role, 
        designJSON: designerJSON, 
        size: 'CR80', 
        orientation: 'landscape',
        // WHERE THE DESIGN CAN BE USED — phase, never academic year. A card
        // design is how a school's cards look, not a record of a session, so it
        // carries no year and keeps working next September. The server resolves
        // WHICH phase from the request context; this only says whether to pin.
        phaseOnly
      };
      
      const created = await fetchJson('/api/id-cards/templates', { method: 'POST', body: JSON.stringify(payload) });
      setSuccess(t('idCard.templateCreated'));
      setTemplates(prev => [created, ...prev]);
      setSelectedTemplateId(String(created.id));
      setTemplateName(created.name || finalName);

      try {
        const design = designerJSON || {};
        const preset = {
          id: `custom-${created?.id || Date.now()}`,
          name: created?.name || finalName,
          metadata: { description: 'Custom preset' },
          size: design?.size || 'CR80',
          orientation: design?.orientation || 'landscape',
          background: design?.background || { color: '#FFFFFF', accentColor: '#1976D2', imageUrl: '' },
          elements: Array.isArray(design?.elements) ? design.elements : []
        };
        upsertCustomPreset(preset);
      } catch (_) {}
    } catch (e) {
      console.error('Template creation error:', e);
      setError(e?.message || t('idCard.errors.createTemplate'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      setLoading(true);
      const trimmed = (templateName || '').trim();
      const finalName = trimmed || (selectedTemplate.name || '').trim();
      
      if (!finalName || finalName.trim() === '') {
        setError(t('idCard.errors.templateNameRequired'));
        return;
      }
      
      // Name, design and scope only. Spreading the whole row back sent id,
      // schoolId, createdAt and every other column straight into `update()`,
      // which is how a template ends up re-parented by a save that was only
      // meant to move a label.
      const payload = { name: finalName, designJSON: designerJSON, phaseOnly };
      const updated = await fetchJson(`/api/id-cards/templates/${selectedTemplate.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      setTemplates(prev => prev.map(tmpl => tmpl.id === updated.id ? updated : tmpl));
      setSuccess(t('idCard.templateUpdated'));
    } catch (e) {
      setError(e?.message || t('idCard.errors.updateTemplate'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      setLoading(true);
      await fetchJson(`/api/id-cards/templates/${selectedTemplate.id}`, { method: 'DELETE' });
      setTemplates(prev => prev.filter(tmpl => tmpl.id !== selectedTemplate.id));
      setSelectedTemplateId(null);
      setSuccess(t('idCard.templateDeleted'));
    } catch (e) {
      setError(e?.message || t('idCard.errors.deleteTemplate'));
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePreview = async () => {
    try {
      setLoading(true);
      // Data-binding: send only IDs; server/client uses placeholders like {name}, {school.name}
      const payload = { templateId: selectedTemplateId, designJSON: designerJSON && Object.keys(designerJSON).length ? designerJSON : undefined, recipients, options: printOptions };
      const preview = await fetchJson('/api/id-cards/generate', { method: 'POST', body: JSON.stringify(payload) });
      setPreviewData(preview);
      setSuccess(t('idCard.previewGenerated'));
      setTab(3);
    } catch (e) {
      setError(e?.message || t('idCard.errors.generatePreview'));
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = useCallback(async (q) => {
    try {
      setUserLoading(true);
      const res = await fetchJson(`/api/users?q=${encodeURIComponent(q || '')}&limit=10`);
      const rows = Array.isArray(res?.rows) ? res.rows : (Array.isArray(res) ? res : []);
      setUserResults(rows);
    } catch (e) {
      setError(e?.message || t('idCard.errors.searchUsers'));
    } finally {
      setUserLoading(false);
    }
  }, []);

  const handleChooseUser = async (userId) => {
    try {
      setLoading(true);
      const payload = { templateId: selectedTemplateId, designJSON: designerJSON && Object.keys(designerJSON).length ? designerJSON : undefined, recipients: [{ userId }], options: { quick: false } };
      const preview = await fetchJson('/api/id-cards/generate', { method: 'POST', body: JSON.stringify(payload) });
      setPreviewData(preview);
      setUserPickerOpen(false);
      setTab(3);
    } catch (e) {
      setError(e?.message || t('idCard.errors.loadPreview'));
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPreview = async () => {
    try {
      setLoading(true);
      if (Array.isArray(recipients) && recipients.length > 0) {
        const payload = { templateId: selectedTemplateId, designJSON: designerJSON && Object.keys(designerJSON).length ? designerJSON : undefined, recipients, options: { quick: true } };
        const preview = await fetchJson('/api/id-cards/generate', { method: 'POST', body: JSON.stringify(payload) });
        setQuickPreviewData(preview);
      } else {
        const local = { design: designerJSON && Object.keys(designerJSON).length ? designerJSON : (selectedTemplateId ? (templates.find(tmpl => String(tmpl.id) === String(selectedTemplateId))?.designJSON || {}) : {}), recipients: [], options: { quick: true }, sampleBinding: {} };
        setQuickPreviewData(local);
      }
      setQuickPreviewOpen(true);
    } catch (e) {
      setError(e?.message || t('idCard.errors.preview'));
    } finally {
      setLoading(false);
    }
  };

  const renderIdCardPreview = (data) => {
    const design = data?.design || designerJSON || {};
    const bindings = Array.isArray(data?.bindings) && data.bindings.length > 0 ? data.bindings : [data?.sampleBinding || {}];
    const bg = design?.background?.color || '#fff';
    const els = design?.elements || [];

    const resolveText = (binding, tmpl) => String(tmpl || '').replace(/\{[^}]+\}/g, (tok) => (binding[tok] ?? tok));
    const resolveData = (binding, tmpl) => String(tmpl || '').replace(/\{[^}]+\}/g, (tok) => (binding[tok] ?? ''));
    const resolveSrc = (binding, src) => {
      const placeholderLogo = '/placeholderImg.png';
      const placeholderPhoto = '/placeholderImg.png';
      if (!src || typeof src !== 'string') return placeholderPhoto;
      if (src.startsWith('{') && src.endsWith('}')) {
        if (src === '{school.logoUrl}') return toAbsoluteUrl(schoolBrand.logoUrl || binding['{school.logoUrl}'] || placeholderLogo);
        return toAbsoluteUrl(binding[src] || placeholderPhoto);
      }
      return toAbsoluteUrl(src);
    };

    const cardWidthPx = 360;
    const cardHeightPx = Math.round(cardWidthPx / 1.586);

    return (
      <Box>
        {bindings.map((binding, idx) => (
          <Box key={idx} sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f8fafc', display: 'inline-block' }}>
              <Box sx={{ position: 'relative', width: `${cardWidthPx}px`, height: `${cardHeightPx}px`, background: bg, overflow: 'hidden', borderRadius: 1 }}>
                {els.map((el) => {
                  const style = { position: 'absolute', left: `${el.x}%`, top: `${el.y}%`, width: `${el.w}%`, height: `${el.h}%` };
                  if (el.type === 'shape') {
                    const border = (el.borderWidth || 0) > 0 ? `${el.borderWidth}px solid ${el.borderColor || '#000000'}` : 'none';
                    const styleShape = { ...style, backgroundColor: el.color || '#1976D2', opacity: el.opacity === 0 ? 0 : (el.opacity || 1), border, transform: `rotate(${el.rotate || 0}deg)` };
                    let borderRadiusCss = `${el.radius || 0}px`;
                    if (el.shapeType === 'circle') borderRadiusCss = '50%';
                    return <Box key={el.id} sx={{ ...styleShape, borderRadius: borderRadiusCss }} />;
                  }
                  if (el.type === 'line') {
                    const thickness = el.thicknessPx || 2;
                    const hPct = Math.max((thickness / cardHeightPx) * 100, 0.2);
                    const baseStyle = { position: 'absolute', left: `${el.x}%`, top: `${el.y}%`, width: `${el.w}%`, height: `${hPct}%` };
                    return (
                      <Box key={el.id} sx={{ ...baseStyle, bgcolor: el.color || '#111827', transform: `rotate(${el.rotate || 0}deg)`, transformOrigin: 'left center' }} />
                    );
                  }
                  if (el.type === 'text') {
                    let justify = 'flex-start';
                    if ((el.textAlign || 'left') === 'center') justify = 'center';
                    if (el.textAlign === 'right') justify = 'flex-end';
                    const text = resolveText(binding, el.text);
                    return (
                      <Box key={el.id} sx={{ ...style, color: el.color || '#000', fontSize: `${(el.fontSize || 6)}pt`, fontWeight: el.fontWeight || 400, display: 'flex', alignItems: 'center', justifyContent: justify, textAlign: el.textAlign || 'left', px: 0.5 }}>
                        {text}
                      </Box>
                    );
                  }
                  if (el.type === 'logo' || el.type === 'photo') {
                    const src = resolveSrc(binding, el.src || '');
                    return <Box key={el.id} sx={{ ...style, backgroundImage: `url("${src}")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: el.type === 'logo' ? 'contain' : 'cover', borderRadius: el.type === 'photo' ? `${el.radius || 0}px` : 0 }} />;
                  }
                  if (el.type === 'qr') {
                    const value = resolveData(binding, el.data);
                    return (
                      <Box key={el.id} sx={{ ...style, overflow: 'hidden' }}>
                        <QRCodeBox value={value} />
                      </Box>
                    );
                  }
                  if (el.type === 'barcode') {
                    const value = resolveData(binding, el.data);
                    return (
                      <Box key={el.id} sx={{ ...style, overflow: 'hidden' }}>
                        <BarcodeBox value={value} />
                      </Box>
                    );
                  }
                  return null;
                })}
              </Box>
            </Paper>
          </Box>
        ))}
      </Box>
    );
  };

  // expose guide setters for render hook
  useEffect(() => {
    window.__setGuides = (v, h) => { window.__guidesV = Array.from(new Set(v)); window.__guidesH = Array.from(new Set(h)); };
    return () => {
      delete window.__setGuides; delete window.__guidesV; delete window.__guidesH;
    };
  }, []);

  const handlePrepareIssueQueue = async () => {
    const all = Array.isArray(recipients) ? recipients : [];
    if (all.length === 0) return;
    if (validRecipients.length === 0) {
      setError(t('idCard.errors.noRecipientsWithUserAccount') || 'Selected recipients do not have linked user accounts.');
      return;
    }
    if (validRecipients.length !== all.length) {
      setError(t('idCard.errors.someRecipientsMissingUserAccount', { count: all.length - validRecipients.length }) || 'Some selected students do not have linked user accounts and were skipped.');
    }

    const suggestion = await handleNextCardNo(cardPrefix);
    if (!suggestion) {
      setError(t('idCard.errors.nextCardNo') || 'Failed to compute next card number');
      return;
    }
    setNextSuggestion(suggestion);
    // Extract numeric suffix
    const match = (suggestion || '').match(/(\d+)$/);
    const startNum = match ? parseInt(match[1], 10) : 1;
    const padLen = match ? match[1].length : 4;
    const prefix = cardPrefix || (suggestion ? suggestion.replace(/\d+$/, '') : '');
    const items = validRecipients.map((r, idx) => ({
      userId: r.userId,
      cardNo: `${prefix}${String(startNum + idx).padStart(padLen, '0')}`,
      barcodeData: r.barcodeData || null,
      qrData: r.qrData || null,
      metadata: r.metadata || null,
    }));
    setIssueQueue(items);
    setSuccess(t('idCard.snack.prepared', { count: items.length }));
  };

  const handleIssueCards = async () => {
    try {
      const items = Array.isArray(issueQueue) ? issueQueue : [];
      if (!selectedTemplateId) {
        setError(t('idCard.errors.templateRequired') || 'Please select a template');
        return;
      }
      if (items.length === 0) {
        setError(t('idCard.errors.noIssueItems') || 'No issue items prepared');
        return;
      }
      const bad = items.find((it) => !it?.userId || !(String(it?.cardNo || '').trim()));
      if (bad) {
        setError(t('idCard.errors.invalidIssueItems') || 'Some issue items are missing user or card number. Please prepare issue items again.');
        return;
      }
      setLoading(true);
      const payload = { templateId: selectedTemplateId, items };
      const result = await fetchJson('/api/id-cards/issue', { method: 'POST', body: JSON.stringify(payload) });
      setSuccess(t('idCard.snack.issued', { count: result?.count || issueQueue.length }));
      setIssueQueue([]);
      setTab(4);
    } catch (e) {
      setError(e?.message || t('idCard.errors.issueCards'));
    } finally {
      setLoading(false);
    }
  };

  const handleNextCardNo = async (prefix) => {
    try {
      const data = await fetchJson(`/api/id-cards/next-card-no?prefix=${encodeURIComponent(prefix || '')}`);
      return data?.nextCardNo || '';
    } catch (e) {
      setError(e?.message || t('idCard.errors.nextCardNo'));
      return '';
    }
  };

  // Auto-fetch next card number when the Generate tab is opened
  useEffect(() => {
    if (tab !== 2) return;
    const timer = setTimeout(async () => {
      const next = await handleNextCardNo(cardPrefix);
      if (next) setNextSuggestion(next);
    }, 350);
    return () => clearTimeout(timer);
  }, [tab, cardPrefix]);

  useEffect(() => {
    const loadHistory = async () => {
      if (tab !== 4) return;
      try {
        setHistoryLoading(true);
        const data = await fetchJson('/api/id-cards/history');
        const rows = Array.isArray(data?.rows) ? data.rows : (Array.isArray(data) ? data : []);
        const count = data?.count || rows.length || 0;
        setHistory({ rows, count });
      } catch (e) {
        setError(e?.message || t('idCard.errors.loadHistory'));
      } finally {
        setHistoryLoading(false);
      }
    };
    loadHistory();
  }, [tab]);

  return (
    <Container maxWidth={false} disableGutters sx={{ px: 1, py: 2 }}>
      <Typography variant="h4" gutterBottom>{t('idCard.title')}</Typography>

      <Paper sx={{ p: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
          <Tab label={<Box sx={{ textAlign: 'center', lineHeight: 1.3 }}><Typography variant="caption" display="block" sx={{ opacity: 0.65 }}>Step 1</Typography>{t('idCard.tabs.setup')}</Box>} />
          <Tab label={<Box sx={{ textAlign: 'center', lineHeight: 1.3 }}><Typography variant="caption" display="block" sx={{ opacity: 0.65 }}>Step 2</Typography>{t('idCard.tabs.templates')}</Box>} />
          <Tab label={<Box sx={{ textAlign: 'center', lineHeight: 1.3 }}><Typography variant="caption" display="block" sx={{ opacity: 0.65 }}>Step 3</Typography>{t('idCard.tabs.generate')}</Box>} />
          <Tab label={<Box sx={{ textAlign: 'center', lineHeight: 1.3 }}><Typography variant="caption" display="block" sx={{ opacity: 0.65 }}>Step 4</Typography>{t('idCard.tabs.previewPrint')}</Box>} />
          <Tab label={<Box sx={{ textAlign: 'center', lineHeight: 1.3 }}><Typography variant="caption" display="block" sx={{ opacity: 0.65 }}>Step 5</Typography>{t('idCard.tabs.history')}</Box>} />
        </Tabs>

        <Divider sx={{ my: 2 }} />

        <TabPanel value={tab} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label={t('idCard.schoolName')} fullWidth value={schoolBrand.name} onChange={(e) => setSchoolBrand({ ...schoolBrand, name: e.target.value })} helperText={t('idCard.helpers.schoolName') || 'Appears on all generated ID cards'} />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" component="label">
                  {t('idCard.uploadLogo')}
                  <input type="file" hidden accept="image/*" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const form = new FormData();
                    form.append('file', file);
                    try {
                      const data = await fetchJson('/api/id-cards/assets/logo', { method: 'POST', body: form });
                      const abs = toAbsoluteUrl(data.url);
                      setSchoolBrand(prev => ({ ...prev, logoUrl: abs }));
                    } catch (err) {
                      setError(err.message || t('idCard.errors.uploadFailed'));
                    }
                  }} />
                </Button>
                {schoolBrand.logoUrl && <Box component="img" src={schoolBrand.logoUrl} alt="logo" sx={{ height: 40, ml: 1 }} />}
              </Stack>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <FormControl size="small">
              <InputLabel>{t('idCard.roleScope')}</InputLabel>
              <Select label={t('idCard.roleScope')} value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} sx={{ minWidth: 180 }}>
                <MenuItem value="Student">{t('idCard.roles.student')}</MenuItem>
                <MenuItem value="Teacher">{t('idCard.roles.teacher')}</MenuItem>
                <MenuItem value="Staff">{t('idCard.roles.staff')}</MenuItem>
              </Select>
              <FormHelperText>{t('idCard.helpers.roleScope') || 'Scope this template to a specific role type'}</FormHelperText>
            </FormControl>
            <Button variant="contained" onClick={handleCreateTemplate} disabled={loading}>{t('idCard.newTemplate')}</Button>
            <Button variant="outlined" onClick={handleUpdateTemplate} disabled={!selectedTemplate || loading}>{t('idCard.saveTemplate')}</Button>
            <Button color="error" variant="outlined" onClick={handleDeleteTemplate} disabled={!selectedTemplate || loading}>{t('common.delete')}</Button>
            <TextField size="small" label={t('idCard.templateName')} value={templateName} onChange={(e) => setTemplateName(e.target.value)} sx={{ minWidth: 220 }} helperText={t('idCard.helpers.templateName') || 'Give this design a unique, recognizable name'} />
            <FormControlLabel
              control={<Checkbox checked={phaseOnly} onChange={(e) => setPhaseOnly(e.target.checked)} />}
              label={t('idCard.phaseOnly', { defaultValue: 'This phase only' })}
              title={t('idCard.helpers.phaseOnly', {
                defaultValue: 'Off means every phase in the school can use this design. Designs are never tied to an academic year — this one stays available for next year’s cards.'
              })}
            />
            <Button variant="outlined" onClick={handleCreateTemplate} disabled={loading}>{t('idCard.saveAsNew')}</Button>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('idCard.template')}</InputLabel>
                <Select label={t('idCard.template')} value={selectedTemplateId || ''} onChange={(e) => setSelectedTemplateId(e.target.value)} displayEmpty renderValue={(val) => {
                  if (!val) return t('idCard.noSavedTemplates');
                  const tmpl = templates.find(x => String(x.id) === String(val));
                  return tmpl ? tmpl.name : t('idCard.noSavedTemplates');
                }}>
                  {templates.length === 0 && (
                    <MenuItem value="" disabled>{t('idCard.noSavedTemplates')}</MenuItem>
                  )}
                  {templates.map(tmpl => (
                    <MenuItem key={tmpl.id} value={String(tmpl.id)}>{tmpl.name} <Chip label={tmpl.roleScope || t('idCard.any')} size="small" sx={{ ml: 1 }} /></MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('idCard.helpers.templateSelect') || 'Load a saved design to edit or use for generation'}</FormHelperText>
              </FormControl>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>{t('idCard.choosePreset')}</Typography>
                <Grid container spacing={1}>
                  {allPresets.map(preset => (
                    <Grid item xs={6} key={preset.id}>
                      <Card>
                        <CardActionArea onClick={() => { 
                          setDesignerJSON({ background: preset.background, elements: preset.elements, size: preset.size, orientation: preset.orientation }); 
                          setTemplateName(preset.name); 
                          setSelectedTemplateId(null); 
                        }}>
                          <CardContent>
                            <Typography variant="body2" fontWeight={600}>{preset.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{preset.metadata?.description}</Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">{t('idCard.canvas')}</Typography>
                  <Button size="small" variant="outlined" onClick={handleUndo} disabled={!canUndo || loading}>{t('idCard.undo')}</Button>
                </Stack>
                <Box sx={{ width: '100%', overflowX: 'hidden', display: 'flex', justifyContent: 'center' }}>
                  <Box ref={canvasRef} onMouseDown={(e) => {
                    // start marquee selection on empty canvas
                    const rect = getCanvasRect();
                    if (!rect) return;
                    const startX = ((e.clientX - rect.left) / rect.width) * 100;
                    const startY = ((e.clientY - rect.top) / rect.height) * 100;
                    setSelectedElId(null);
                    setSelectedIds([]);
                    setSelectionBox({ active: true, x1: clamp(startX, 0, 100), y1: clamp(startY, 0, 100), x2: clamp(startX, 0, 100), y2: clamp(startY, 0, 100) });
                    setDragState({ mode: 'marquee', startX: e.clientX, startY: e.clientY, elStart: null });
                  }} sx={{ position: 'relative', width: '100%', maxWidth: '800px', pt: '63.05%', background: designerJSON?.background?.color || '#fff', overflow: 'hidden', borderRadius: 1 }}>
                  {(designerJSON?.elements || []).map(el => {
                    const isSelected = (selectedIds && selectedIds.includes(el.id)) || selectedElId === el.id;
                    const style = { position: 'absolute', left: `${el.x}%`, top: `${el.y}%`, width: `${el.w}%`, height: `${el.h}%`, outline: isSelected ? '2px solid #1976D2' : 'none', cursor: dragState.mode !== 'none' ? 'grabbing' : 'move' };
                    const commonProps = { key: el.id, onMouseDown: (e) => onElementMouseDown(e, el, 'drag'), sx: style };
                    if (el.type === 'shape') {
                      const border = (el.borderWidth || 0) > 0 ? `${el.borderWidth}px solid ${el.borderColor || '#000000'}` : 'none';
                      const styleShape = { ...style, backgroundColor: el.color || '#1976D2', opacity: el.opacity === 0 ? 0 : (el.opacity || 1), border, transform: `rotate(${el.rotate || 0}deg)` };
                      let borderRadiusCss = `${el.radius || 0}px`;
                      if (el.shapeType === 'circle') {
                        borderRadiusCss = '50%';
                      }
                      return (
                        <Box {...commonProps} sx={{ ...styleShape, borderRadius: borderRadiusCss }} />
                      );
                    }
                    if (el.type === 'line') {
                      const thickness = el.thicknessPx || 2;
                      const baseStyle = { position: 'absolute', left: `${el.x}%`, top: `${el.y}%`, width: `${el.w}%`, height: `${Math.max(thickness / (getCanvasRect()?.height || 1) * 100, 0.2)}%` };
                      return (
                        <Box key={el.id} onMouseDown={(e) => onElementMouseDown(e, el, 'drag')} sx={{ ...baseStyle, bgcolor: el.color || '#111827', transform: `rotate(${el.rotate || 0}deg)`, transformOrigin: 'left center' }} />
                      );
                    }
                    if (el.type === 'text') {
                      let justify = 'flex-start';
                      if ((el.textAlign || 'left') === 'center') justify = 'center';
                      if (el.textAlign === 'right') justify = 'flex-end';
                      return (
                        <Box {...commonProps} sx={{ ...style, color: el.color || '#000', fontSize: `${(el.fontSize || 6)}pt`, fontWeight: el.fontWeight || 400, display: 'flex', alignItems: 'center', justifyContent: justify, textAlign: el.textAlign || 'left', px: 0.5 }}>
                          {renderTextContent(el.text || '')}
                        </Box>
                      );
                    }
                    if (el.type === 'logo') {
                      const srcRaw = selectedElement?.id === el.id && selectedElement.src !== undefined ? selectedElement.src : (el.src || '{school.logoUrl}');
                      const src = resolveImageSrc(srcRaw);
                      return (
                        <Box {...commonProps} component="img" src={src} alt="logo" crossOrigin="anonymous" sx={{ ...style, objectFit: 'contain' }} />
                      );
                    }
                    if (el.type === 'photo') {
                      const srcRaw = selectedElement?.id === el.id && selectedElement.src !== undefined ? selectedElement.src : (el.src || '');
                      const src = resolveImageSrc(srcRaw);
                      return (
                        <Box {...commonProps} component="img" src={src} alt="photo" crossOrigin="anonymous" sx={{ ...style, objectFit: 'cover', borderRadius: `${el.radius || 0}px` }} />
                      );
                    }
                    if (el.type === 'qr') {
                      const qrVal = renderTextContent(el.data || '{qr.fullInfo}') || 'QR';
                      return (
                        <Box {...commonProps} sx={{ ...style, overflow: 'hidden' }}>
                          <QRCodeBox value={qrVal} />
                        </Box>
                      );
                    }
                    if (el.type === 'barcode') {
                      const bcVal = renderTextContent(el.data || '{studentNumber}') || '0000001';
                      return (
                        <Box {...commonProps} sx={{ ...style, overflow: 'hidden' }}>
                          <BarcodeBox value={bcVal} />
                        </Box>
                      );
                    }
                    return null;
                  })}
                  {(() => {
                    const ids = selectedIds.length ? selectedIds : (selectedElId ? [selectedElId] : []);
                    if (!ids.length) return null;
                    const sels = ids.map(id => elementsById[id]).filter(Boolean);
                    if (!sels.length) return null;
                    const minX = Math.min(...sels.map(s => s.x));
                    const minY = Math.min(...sels.map(s => s.y));
                    const maxX = Math.max(...sels.map(s => s.x + s.w));
                    const maxY = Math.max(...sels.map(s => s.y + s.h));
                    const handleLeft = maxX;
                    const handleTop = maxY;
                    return (
                      <Box sx={{ position: 'absolute', left: `${handleLeft}%`, top: `${handleTop}%`, width: 12, height: 12, bgcolor: '#1976D2', borderRadius: 0.5, transform: 'translate(-50%, -50%)', cursor: 'nwse-resize' }} onMouseDown={onResizeHandleMouseDown} />
                    );
                  })()}
                  {/* Marquee selection rectangle */}
                  {selectionBox.active && (() => {
                    const left = Math.min(selectionBox.x1, selectionBox.x2);
                    const top = Math.min(selectionBox.y1, selectionBox.y2);
                    const width = Math.abs(selectionBox.x2 - selectionBox.x1);
                    const height = Math.abs(selectionBox.y2 - selectionBox.y1);
                    return (
                      <Box sx={{ position: 'absolute', left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%`, border: '1px dashed rgba(25,118,210,0.8)', backgroundColor: 'transparent', pointerEvents: 'none' }} />
                    );
                  })()}
                  {/* Smart Guides (non-blocking, thin) */}
                  {(() => {
                    const g = window.__smartGuides || [];
                    return (
                      <>
                        {g.filter(x => x.axis === 'v').map((x, i) => (
                          <Box key={`vg-${i}`} sx={{ position: 'absolute', left: `calc(${x.pos}% - 0.5px)`, top: 0, width: '1px', height: '100%', background: 'rgba(255,0,170,0.9)', pointerEvents: 'none' }} />
                        ))}
                        {g.filter(x => x.axis === 'h').map((x, i) => (
                          <Box key={`hg-${i}`} sx={{ position: 'absolute', top: `calc(${x.pos}% - 0.5px)`, left: 0, height: '1px', width: '100%', background: 'rgba(0,209,178,0.9)', pointerEvents: 'none' }} />
                        ))}
                        {(() => {
                          const lbl = window.__smartLabelLT;
                          if (!lbl) return null;
                          return (
                            <Box sx={{ position: 'absolute', left: `calc(${lbl.xPct}% + 4px)`, top: `calc(${lbl.yPct}% + 4px)`, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, px: 0.5, py: 0.25, borderRadius: 0.5, pointerEvents: 'none' }}>
                              {lbl.text}
                            </Box>
                          );
                        })()}
                      </>
                    );
                  })()}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>{t('idCard.properties')}</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6} md={3}><TextField size="small" label={t('idCard.props.bgColor')} type="color" value={designerJSON?.background?.color || '#ffffff'} onChange={(e) => setDesignerJSON(prev => { pushUndo(prev); return ({ ...prev, background: { ...(prev?.background || {}), color: e.target.value } }); })} fullWidth /></Grid>
                  <Grid item xs={6} md={3}><TextField size="small" label={t('idCard.props.accent')} type="color" value={designerJSON?.background?.accentColor || '#1976D2'} onChange={(e) => setDesignerJSON(prev => { pushUndo(prev); return ({ ...prev, background: { ...(prev?.background || {}), accentColor: e.target.value } }); })} fullWidth /></Grid>
                  <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button variant="outlined" component="label" size="small">
                        {t('idCard.uploadLogo')}
                        <input type="file" hidden accept="image/*" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const form = new FormData();
                          form.append('file', file);
                          try {
                            const data = await fetchJson('/api/id-cards/assets/logo', { method: 'POST', body: form });
                            const abs = toAbsoluteUrl(data.url);
                            setSchoolBrand(prev => ({ ...prev, logoUrl: abs }));
                          } catch (err) {
                            setError(err.message || t('idCard.errors.uploadFailed'));
                          }
                        }} />
                      </Button>
                      {schoolBrand.logoUrl && <Box component="img" src={schoolBrand.logoUrl} alt="logo" sx={{ height: 32 }} />}
                    </Stack>
                  </Grid>
                </Grid>

                {selectedElement?.type === 'text' && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>{t('idCard.placeholders')}</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f9fbff' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{t('idCard.clickPlaceholder')}</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {(() => {
                          const scope = (filters.role || '').toLowerCase();
                          const common = ['{name}', '{role}', '{expiryDate}', '{school.name}', '{school.motto}'];
                          const studentOnly = ['{studentNumber}', '{student.dateOfBirth}', '{student.placeOfBirth}', '{student.sex}', '{student.address}', '{className}', '{guardian.name}', '{guardian.phone}', '{qr.fullInfo}'];
                          const staffOnly = ['{staff.name}', '{staff.role}', '{staff.phone}', '{staff.email}'];
                          const tokens = scope === 'student' ? [...common, ...studentOnly] : [...common, ...staffOnly];
                          return tokens;
                        })().map(tok => (
                          <Chip key={tok} label={tok} size="small" color="secondary" variant="outlined" onClick={() => replaceElement(selectedElement.id, { ...selectedElement, text: `${(selectedElement.text || '')}${tok}` })} sx={{ cursor: 'pointer', borderStyle: 'dashed' }} />
                        ))}
                      </Stack>
                    </Paper>
                  </Box>
                )}
                {selectedElement?.type === 'text' && <Divider sx={{ my: 1 }} />}

                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1, columnGap: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => addElement('text')}>{t('idCard.addText')}</Button>
                  <Button size="small" variant="outlined" onClick={() => addElement('shape')}>{t('idCard.addShape')}</Button>
                  <Button size="small" variant="outlined" onClick={() => addElement('photo')}>{t('idCard.addPhoto')}</Button>
                  <Button size="small" variant="outlined" onClick={() => addElement('logo')}>{t('idCard.addLogo')}</Button>
                  <Button size="small" variant="outlined" onClick={() => addElement('qr')}>{t('idCard.addQR')}</Button>
                  <Button size="small" variant="outlined" onClick={() => addElement('barcode')}>{t('idCard.addBarcode')}</Button>
                  <Button size="small" variant="outlined" onClick={() => addElement('line')}>{t('idCard.addDivider')}</Button>
                  <Button size="small" variant="outlined" disabled={!selectedIds || selectedIds.length < 2} onClick={() => alignSelectedElements('left')}>{t('idCard.align.left')}</Button>
                  <Button size="small" variant="outlined" disabled={!selectedIds || selectedIds.length < 2} onClick={() => alignSelectedElements('centerX')}>{t('idCard.align.center')}</Button>
                  <Button size="small" variant="outlined" disabled={!selectedIds || selectedIds.length < 2} onClick={() => alignSelectedElements('right')}>{t('idCard.align.right')}</Button>
                  <Button size="small" variant="outlined" disabled={!selectedIds || selectedIds.length < 2} onClick={() => alignSelectedElements('top')}>{t('idCard.align.top')}</Button>
                  <Button size="small" variant="outlined" disabled={!selectedIds || selectedIds.length < 2} onClick={() => alignSelectedElements('middle')}>{t('idCard.align.middle')}</Button>
                  <Button size="small" variant="outlined" disabled={!selectedIds || selectedIds.length < 2} onClick={() => alignSelectedElements('bottom')}>{t('idCard.align.bottom')}</Button>
                  <Button size="small" variant="outlined" disabled={!selectedIds || selectedIds.length < 3} onClick={() => distributeSelectedElements('h')}>{t('idCard.distribute.horizontal')}</Button>
                  <Button size="small" variant="outlined" disabled={!selectedIds || selectedIds.length < 3} onClick={() => distributeSelectedElements('v')}>{t('idCard.distribute.vertical')}</Button>
                  <Button size="small" variant="outlined" disabled={!selectedIds || selectedIds.length < 2} onClick={groupSelectedElements}>{t('idCard.group')}</Button>
                  <Button size="small" variant="outlined" disabled={!selectedIds || selectedIds.length === 0} onClick={ungroupSelectedElements}>{t('idCard.ungroup')}</Button>
                  <Button size="small" variant="outlined" disabled={!selectedElement} onClick={duplicateSelectedElement}>{t('idCard.duplicate')}</Button>
                  {selectedElement && <Button size="small" color="error" variant="outlined" onClick={() => deleteElement(selectedElement.id)}>{t('idCard.deleteSelected')}</Button>}
                </Stack>

                {selectedElement && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>{t('idCard.selectedElement')}</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={3}><TextField size="small" label={t('idCard.props.xPct')} type="number" value={Number(selectedElement.x).toFixed(1)} onChange={(e) => {
                        const v = parseFloat(e.target.value)||0;
                        updateSelection(el => ({ x: clamp(v, 0, 100 - el.w) }));
                      }} fullWidth /></Grid>
                      <Grid item xs={3}><TextField size="small" label={t('idCard.props.yPct')} type="number" value={Number(selectedElement.y).toFixed(1)} onChange={(e) => {
                        const v = parseFloat(e.target.value)||0;
                        updateSelection(el => ({ y: clamp(v, 0, 100 - el.h) }));
                      }} fullWidth /></Grid>
                      <Grid item xs={3}><TextField size="small" label={t('idCard.props.wPct')} type="number" value={Number(selectedElement.w).toFixed(1)} onChange={(e) => {
                        const v = parseFloat(e.target.value)||0;
                        updateSelection(el => ({ w: clamp(v, 5, 100 - el.x) }));
                      }} fullWidth /></Grid>
                      <Grid item xs={3}><TextField size="small" label={t('idCard.props.hPct')} type="number" value={Number(selectedElement.h).toFixed(1)} onChange={(e) => {
                        const v = parseFloat(e.target.value)||0;
                        updateSelection(el => ({ h: clamp(v, 5, 100 - el.y) }));
                      }} fullWidth /></Grid>
                      {selectedElement.type === 'text' && (
                        <>
                          <Grid item xs={12}><TextField size="small" label={t('idCard.props.text')} value={selectedElement.text || ''} onChange={(e) => updateSelection({ text: e.target.value })} fullWidth /></Grid>
                          <Grid item xs={4}><TextField size="small" label={t('idCard.props.fontSize')} type="number" value={selectedElement.fontSize || 6} onChange={(e) => updateSelection({ fontSize: parseFloat(e.target.value)||6 })} fullWidth /></Grid>
                          <Grid item xs={4}><TextField size="small" label={t('idCard.props.weight')} type="number" value={selectedElement.fontWeight || 400} onChange={(e) => updateSelection({ fontWeight: parseInt(e.target.value)||400 })} fullWidth /></Grid>
                          <Grid item xs={4}><TextField size="small" label={t('idCard.props.color')} type="color" value={selectedElement.color || '#000000'} onChange={(e) => updateSelection({ color: e.target.value })} fullWidth /></Grid>
                          <Grid item xs={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>{t('idCard.props.align')}</InputLabel>
                              <Select label={t('idCard.props.align')} value={selectedElement.textAlign || 'left'} onChange={(e) => updateSelection({ textAlign: e.target.value })}>
                                <MenuItem value="left">{t('idCard.alignText.left')}</MenuItem>
                                <MenuItem value="center">{t('idCard.alignText.center')}</MenuItem>
                                <MenuItem value="right">{t('idCard.alignText.right')}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                      {selectedElement.type === 'shape' && (
                        <>
                          <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>{t('idCard.props.shape')}</InputLabel>
                              <Select label={t('idCard.props.shape')} value={selectedElement.shapeType || 'rect'} onChange={(e) => replaceElement(selectedElement.id, { ...selectedElement, shapeType: e.target.value, lockAspect: e.target.value === 'circle' ? true : selectedElement.lockAspect })}>
                                <MenuItem value="rect">{t('idCard.shapes.rectangle')}</MenuItem>
                                <MenuItem value="rounded">{t('idCard.shapes.rounded')}</MenuItem>
                                <MenuItem value="circle">{t('idCard.shapes.circle')}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}><TextField size="small" label={t('idCard.props.fillColor')} type="color" value={selectedElement.color || '#1976D2'} onChange={(e) => updateSelection({ color: e.target.value })} fullWidth /></Grid>
                          {selectedElement.shapeType !== 'circle' && (
                            <Grid item xs={4}><TextField size="small" label={t('idCard.props.radius')} type="number" value={selectedElement.radius || 0} onChange={(e) => updateSelection({ radius: parseFloat(e.target.value)||0 })} fullWidth /></Grid>
                          )}
                          <Grid item xs={4}><TextField size="small" label={t('idCard.props.opacity')} type="number" inputProps={{ step: 0.1, min: 0, max: 1 }} value={selectedElement.opacity ?? 1} onChange={(e) => updateSelection({ opacity: Math.max(0, Math.min(1, parseFloat(e.target.value)||0)) })} fullWidth /></Grid>
                          <Grid item xs={4}><TextField size="small" label={t('idCard.props.rotate')} type="number" value={selectedElement.rotate || 0} onChange={(e) => updateSelection({ rotate: parseFloat(e.target.value)||0 })} fullWidth /></Grid>
                          <Grid item xs={4}><TextField size="small" label={t('idCard.props.borderW')} type="number" value={selectedElement.borderWidth || 0} onChange={(e) => updateSelection({ borderWidth: parseFloat(e.target.value)||0 })} fullWidth /></Grid>
                          <Grid item xs={4}><TextField size="small" label={t('idCard.props.borderColor')} type="color" value={selectedElement.borderColor || '#000000'} onChange={(e) => updateSelection({ borderColor: e.target.value })} fullWidth /></Grid>
                          <Grid item xs={4}><FormControlLabel control={<Checkbox checked={!!selectedElement.lockAspect} onChange={(e) => updateSelection({ lockAspect: e.target.checked })} />} label={t('idCard.props.lockAspect')} /></Grid>
                        </>
                      )}
                      {selectedElement.type === 'line' && (
                        <>
                          <Grid item xs={6}><TextField size="small" label={t('idCard.props.color')} type="color" value={selectedElement.color || '#111827'} onChange={(e) => updateSelection({ color: e.target.value })} fullWidth /></Grid>
                          <Grid item xs={3}><TextField size="small" label={t('idCard.props.thickness')} type="number" value={selectedElement.thicknessPx || 2} onChange={(e) => updateSelection({ thicknessPx: parseFloat(e.target.value)||1 })} fullWidth /></Grid>
                          <Grid item xs={3}><TextField size="small" label={t('idCard.props.rotate')} type="number" value={selectedElement.rotate || 0} onChange={(e) => updateSelection({ rotate: parseFloat(e.target.value)||0 })} fullWidth /></Grid>
                        </>
                      )}
                      {selectedElement.type === 'photo' && (
                        <>
                          <Grid item xs={6}><TextField size="small" label={t('idCard.props.cornerRadius')} type="number" value={selectedElement.radius || 0} onChange={(e) => updateSelection({ radius: parseFloat(e.target.value)||0 })} fullWidth /></Grid>
                          <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>{t('idCard.props.imageSource')}</InputLabel>
                              <Select label={t('idCard.props.imageSource')} value={selectedElement.imgBinding || (selectedElement.src === '{student.photoUrl}' ? 'student' : selectedElement.src === '{user.avatarUrl}' ? 'user' : selectedElement.src === '{school.logoUrl}' ? 'school' : 'custom')} onChange={(e) => {
                                const v = e.target.value;
                                const map = { student: '{student.photoUrl}', user: '{user.avatarUrl}', school: '{school.logoUrl}' };
                                replaceElement(selectedElement.id, { ...selectedElement, imgBinding: v, src: map[v] || (selectedElement.src || '') });
                              }}>
                                <MenuItem value="student">{t('idCard.imgSrc.studentPhoto')}</MenuItem>
                                <MenuItem value="user">{t('idCard.imgSrc.userAvatar')}</MenuItem>
                                <MenuItem value="school">{t('idCard.imgSrc.schoolLogo')}</MenuItem>
                                <MenuItem value="custom">{t('idCard.imgSrc.customUrl')}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {(!selectedElement.imgBinding || selectedElement.imgBinding === 'custom') && (
                            <Grid item xs={12}><TextField size="small" label={t('idCard.props.imageUrl')} value={selectedElement.src || ''} onChange={(e) => replaceElement(selectedElement.id, { ...selectedElement, src: e.target.value })} fullWidth /></Grid>
                          )}
                          <Grid item xs={12}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button variant="outlined" component="label" size="small">
                                {t('idCard.uploadPhoto')}
                                <input type="file" hidden accept="image/*" onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const form = new FormData();
                                  form.append('file', file);
                                  try {
                                    const data = await fetchJson('/api/id-cards/assets/logo', { method: 'POST', body: form });
                                    const abs = toAbsoluteUrl(data.url);
                                    replaceElement(selectedElement.id, { ...selectedElement, imgBinding: 'custom', src: abs });
                                  } catch (err) {
                                    setError(err.message || t('idCard.errors.uploadFailed'));
                                  }
                                }} />
                              </Button>
                              {selectedElement.src && <Box component="img" src={resolveImageSrc(selectedElement.src)} alt="preview" sx={{ height: 36 }} />}
                            </Stack>
                          </Grid>
                        </>
                      )}
                      {selectedElement.type === 'logo' && (
                        <>
                          <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                              <InputLabel>{t('idCard.props.imageSource')}</InputLabel>
                              <Select label={t('idCard.props.imageSource')} value={selectedElement.imgBinding || (selectedElement.src === '{school.logoUrl}' ? 'school' : 'custom')} onChange={(e) => {
                                const v = e.target.value;
                                const map = { school: '{school.logoUrl}' };
                                replaceElement(selectedElement.id, { ...selectedElement, imgBinding: v, src: map[v] || (selectedElement.src || '') });
                              }}>
                                <MenuItem value="school">{t('idCard.imgSrc.schoolLogo')}</MenuItem>
                                <MenuItem value="custom">{t('idCard.imgSrc.customUrl')}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {(!selectedElement.imgBinding || selectedElement.imgBinding === 'custom') && (
                            <Grid item xs={12}><TextField size="small" label={t('idCard.props.imageUrl')} value={selectedElement.src || ''} onChange={(e) => replaceElement(selectedElement.id, { ...selectedElement, src: e.target.value })} fullWidth /></Grid>
                          )}
                          <Grid item xs={12}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button variant="outlined" component="label" size="small">
                                {t('idCard.uploadImage')}
                                <input type="file" hidden accept="image/*" onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const form = new FormData();
                                  form.append('file', file);
                                  try {
                                    const data = await fetchJson('/api/id-cards/assets/logo', { method: 'POST', body: form });
                                    const abs = toAbsoluteUrl(data.url);
                                    replaceElement(selectedElement.id, { ...selectedElement, imgBinding: 'custom', src: abs });
                                  } catch (err) {
                                    setError(err.message || t('idCard.errors.uploadFailed'));
                                  }
                                }} />
                              </Button>
                              {selectedElement.src && <Box component="img" src={resolveImageSrc(selectedElement.src)} alt="preview" sx={{ height: 32 }} />}
                            </Stack>
                          </Grid>
                        </>
                      )}
                      {(selectedElement.type === 'qr' || selectedElement.type === 'barcode') && (
                        <>
                          <Grid item xs={12}><TextField size="small" label={t('idCard.props.data')} value={selectedElement.data || ''} onChange={(e) => replaceElement(selectedElement.id, { ...selectedElement, data: e.target.value })} fullWidth /></Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button size="small" variant="contained" onClick={handleQuickPreview} disabled={loading}>{t('idCard.quickPreview')}</Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('idCard.generate.role')}</InputLabel>
                <Select label={t('idCard.generate.role')} value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })}>
                  <MenuItem value="Student">{t('idCard.generate.student')}</MenuItem>
                  <MenuItem value="Teacher">{t('idCard.generate.teacher')}</MenuItem>
                  <MenuItem value="Staff">{t('idCard.generate.staff')}</MenuItem>
                </Select>
                <FormHelperText>{t('idCard.helpers.generate.role') || 'Filter recipients by their role in the school'}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              {String(filters.role).toLowerCase() === 'student' ? (
                <FormControl fullWidth size="small">
                  <InputLabel>{t('idCard.generate.class')}</InputLabel>
                  <Select label={t('idCard.generate.class')} value={filters.classId} onChange={(e) => setFilters({ ...filters, classId: e.target.value })}>
                    <MenuItem value="">{t('idCard.generate.selectClass')}</MenuItem>
                    {classes.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{t('idCard.helpers.generate.class') || 'Select a class to load its students below'}</FormHelperText>
                </FormControl>
              ) : (
                <TextField label={t('idCard.generate.department')} fullWidth size="small" value={filters.classId} onChange={(e) => setFilters({ ...filters, classId: e.target.value })} helperText={t('idCard.helpers.generate.department') || 'Filter recipients by department'} />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('idCard.generate.status')}</InputLabel>
                <Select label={t('idCard.generate.status')} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                  <MenuItem value="active">{t('idCard.generate.active')}</MenuItem>
                  <MenuItem value="inactive">{t('idCard.generate.inactive')}</MenuItem>
                </Select>
                <FormHelperText>{t('idCard.helpers.generate.status') || 'Filter students by enrollment status'}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label={t('idCard.generate.search')} fullWidth size="small" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} helperText={t('idCard.helpers.generate.search') || 'Search by name or student number'} />
            </Grid>
            <Grid item xs={12}>
              {String(filters.role).toLowerCase() === 'student' ? (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                  <FormControl fullWidth size="small" disabled={!filters.classId}>
                    <InputLabel>{t('idCard.generate.students')}</InputLabel>
                    <Select multiple label={t('idCard.generate.students')} value={selectedStudentIds} onChange={(e) => setSelectedStudentIds(e.target.value)} renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => {
                          const s = classStudents.find(x => x.id === id);
                          const name = s ? `${s.firstName || s.firstname || ''} ${s.lastName || s.lastname || ''}`.trim() || s.name || s.studentNumber : id;
                          return <Chip key={id} label={name} size="small" />;
                        })}
                      </Box>
                    )}>
                      {classStudents.map((s) => {
                        const name = `${s.firstName || s.firstname || ''} ${s.lastName || s.lastname || ''}`.trim() || s.name || s.studentNumber;
                        return <MenuItem key={s.id} value={s.id}>{name}</MenuItem>;
                      })}
                    </Select>
                    <FormHelperText>{t('idCard.helpers.generate.students') || 'Pick individual students or click \'Use Entire Class\''}</FormHelperText>
                  </FormControl>
                  <Button variant="outlined" onClick={() => setSelectedStudentIds(classStudents.map(s => s.id))} disabled={!filters.classId || classStudents.length === 0}>{t('idCard.generate.useEntireClass')}</Button>
                </Stack>
              ) : (
                <TextField label={t('idCard.generate.recipientIds')} fullWidth size="small" placeholder="e.g. 12, 34, 56" onBlur={(e) => {
                  const ids = (e.target.value || '').split(',').map(s => Number(s.trim())).filter(Boolean).map(id => ({ userId: id }));
                  setRecipients(ids);
                }} />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label={t('idCard.generate.cardNoPrefix')} fullWidth size="small" value={cardPrefix} onChange={(e) => setCardPrefix(e.target.value)} onBlur={async (e) => {
                const next = await handleNextCardNo(e.target.value);
                setNextSuggestion(next);
              }} helperText={t('idCard.helpers.generate.cardNoPrefix') || 'Optional prefix for card numbers, e.g. "STU-". Leave blank for numeric only.'} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('idCard.generate.nextCardNo') || 'Next Card Number'}
                fullWidth
                size="small"
                value={nextSuggestion}
                InputProps={{ readOnly: true }}
                helperText={t('idCard.generate.nextCardNoHint') || 'Auto-suggested next card number'}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={2}>
                <TextField label={t('idCard.generate.rows')} type="number" size="small" value={printOptions.rows} onChange={(e) => setPrintOptions({ ...printOptions, rows: Number(e.target.value) })} helperText={t('idCard.helpers.generate.rows') || 'Cards per column'} />
                <TextField label={t('idCard.generate.cols')} type="number" size="small" value={printOptions.cols} onChange={(e) => setPrintOptions({ ...printOptions, cols: Number(e.target.value) })} helperText={t('idCard.helpers.generate.cols') || 'Cards per row'} />
                <TextField label={t('idCard.generate.gapMm')} type="number" size="small" value={printOptions.gapMm} onChange={(e) => setPrintOptions({ ...printOptions, gapMm: Number(e.target.value) })} helperText={t('idCard.helpers.generate.gapMm') || 'Gap between cards (mm)'} />
                <TextField label={t('idCard.generate.marginMm')} type="number" size="small" value={printOptions.marginMm} onChange={(e) => setPrintOptions({ ...printOptions, marginMm: Number(e.target.value) })} helperText={t('idCard.helpers.generate.marginMm') || 'Page margin (mm)'} />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={handlePrepareIssueQueue} disabled={loading || recipients.length === 0}>{t('idCard.prepareIssueItems')}</Button>
                <Button variant="contained" onClick={handleGeneratePreview} disabled={loading || !selectedTemplateId}>{t('idCard.generatePreview')}</Button>
                <Button variant="outlined" onClick={handleIssueCards} disabled={loading || !selectedTemplateId || issueQueue.length === 0}>{t('idCard.generate.issueCards', { count: issueQueue.length })}</Button>
              </Stack>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={3}>
          {!previewData ? (
            <Typography color="text.secondary">{t('idCard.noPreviewYet')}</Typography>
          ) : (
            <Box>
              <Typography variant="subtitle1" gutterBottom>{t('idCard.preview')}</Typography>
              <Paper variant="outlined" sx={{ p: 2, overflow: 'auto' }}>
                {(() => {
                  const design = previewData.design || designerJSON || {};
                  const bindings = Array.isArray(previewData.bindings) && previewData.bindings.length > 0 ? previewData.bindings : [previewData.sampleBinding || {}];
                  const bg = design?.background?.color || '#fff';
                  const els = design?.elements || [];
                  const card = (binding, idx) => {
                    const resolveSrc = (src) => {
                      const placeholderLogo = '/placeholderImg.png';
                      const placeholderPhoto = '/placeholderImg.png';
                      if (!src || typeof src !== 'string') return placeholderPhoto;
                      if (src.startsWith('{') && src.endsWith('}')) {
                        if (src === '{school.logoUrl}') return toAbsoluteUrl(schoolBrand.logoUrl || binding['{school.logoUrl}'] || placeholderLogo);
                        return toAbsoluteUrl(binding[src] || placeholderPhoto);
                      }
                      return toAbsoluteUrl(src);
                    };
                    const resolveText = (tmpl) => String(tmpl || '').replace(/\{[^}]+\}/g, (tok) => (binding[tok] ?? tok));
                    const resolveData = (tmpl) => String(tmpl || '').replace(/\{[^}]+\}/g, (tok) => (binding[tok] ?? ''));
                    const cardWidthPx = 360;
                    const cardHeightPx = Math.round(cardWidthPx / 1.586);
                    return (
                      <Box key={idx} sx={{ display: 'inline-block', mr: 2, mb: 2 }}>
                        <Box ref={(el) => { previewCardRefs.current[idx] = el; }} sx={{ position: 'relative', width: `${cardWidthPx}px`, height: `${cardHeightPx}px`, background: bg, overflow: 'hidden', borderRadius: 1 }}>
                          {els.map((el) => {
                            const style = { position: 'absolute', left: `${el.x}%`, top: `${el.y}%`, width: `${el.w}%`, height: `${el.h}%` };
                            if (el.type === 'shape') {
                              const border = (el.borderWidth || 0) > 0 ? `${el.borderWidth}px solid ${el.borderColor || '#000000'}` : 'none';
                              const styleShape = { ...style, backgroundColor: el.color || '#1976D2', opacity: el.opacity === 0 ? 0 : (el.opacity || 1), border, transform: `rotate(${el.rotate || 0}deg)` };
                              let borderRadiusCss = `${el.radius || 0}px`;
                              if (el.shapeType === 'circle') borderRadiusCss = '50%';
                              return <Box key={el.id} sx={{ ...styleShape, borderRadius: borderRadiusCss }} />;
                            }
                            if (el.type === 'line') {
                              const thickness = el.thicknessPx || 2;
                              const hPct = Math.max((thickness / cardHeightPx) * 100, 0.2);
                              const baseStyle = { position: 'absolute', left: `${el.x}%`, top: `${el.y}%`, width: `${el.w}%`, height: `${hPct}%` };
                              return (
                                <Box key={el.id} sx={{ ...baseStyle, bgcolor: el.color || '#111827', transform: `rotate(${el.rotate || 0}deg)`, transformOrigin: 'left center' }} />
                              );
                            }
                            if (el.type === 'text') {
                              let justify = 'flex-start';
                              if ((el.textAlign || 'left') === 'center') justify = 'center';
                              if (el.textAlign === 'right') justify = 'flex-end';
                              const text = resolveText(el.text);
                              return (
                                <Box key={el.id} sx={{ ...style, color: el.color || '#000', fontSize: `${(el.fontSize || 6)}pt`, fontWeight: el.fontWeight || 400, display: 'flex', alignItems: 'center', justifyContent: justify, textAlign: el.textAlign || 'left', px: 0.5 }}>
                                  {text}
                                </Box>
                              );
                            }
                            if (el.type === 'logo' || el.type === 'photo') {
                              const src = resolveSrc(el.src || '');
                              return <Box key={el.id} sx={{ ...style, backgroundImage: `url("${src}")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: el.type === 'logo' ? 'contain' : 'cover', borderRadius: el.type === 'photo' ? `${el.radius || 0}px` : 0 }} />;
                            }
                            if (el.type === 'qr') {
                              const value = resolveData(el.data);
                              return (
                                <Box key={el.id} sx={{ ...style, overflow: 'hidden' }}>
                                  <QRCodeBox value={value} />
                                </Box>
                              );
                            }
                            if (el.type === 'barcode') {
                              const value = resolveData(el.data);
                              return (
                                <Box key={el.id} sx={{ ...style, overflow: 'hidden' }}>
                                  <BarcodeBox value={value} />
                                </Box>
                              );
                            }
                            return null;
                          })}
                        </Box>
                      </Box>
                    );
                  };
                  return (
                    <Box>
                      {bindings.map((b, i) => card(b, i))}
                    </Box>
                  );
                })()}
              </Paper>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={() => {
                  const role = String(filters.role || '').toLowerCase();
                  if (role === 'student' && selectedStudentIds.length > 0) {
                    const preloaded = classStudents
                      .filter(s => selectedStudentIds.includes(s.id) && s.userId)
                      .map(s => ({
                        id: s.userId,
                        name: `${s.firstName || s.firstname || ''} ${s.lastName || s.lastname || ''}`.trim() || s.name || s.studentNumber || String(s.id),
                        email: s.email || '',
                        role: t('idCard.generate.student', 'Student'),
                      }));
                    if (preloaded.length > 0) setUserResults(preloaded);
                  } else if (recipients.length > 0) {
                    const preloaded = recipients
                      .filter(r => r.userId)
                      .map(r => ({
                        id: r.userId,
                        name: r.name || `User #${r.userId}`,
                        email: r.email || '',
                        role: r.role || (role === 'teacher' ? t('idCard.generate.teacher', 'Teacher') : t('idCard.generate.staff', 'Staff')),
                      }));
                    if (preloaded.length > 0) setUserResults(preloaded);
                  }
                  setUserPickerOpen(true);
                }}>{t('idCard.chooseUser')}</Button>
                <Button variant="contained" onClick={handlePrintCards} disabled={loading || !previewData}>{t('idCard.print')}</Button>
                <Button variant="outlined" onClick={handleDownloadPdf} disabled={loading || !previewData}>{t('idCard.downloadPdf')}</Button>
                <Button variant="outlined" onClick={() => setTab(2)}>{t('idCard.backToGenerate')}</Button>
              </Stack>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tab} index={4}>
          {historyLoading ? (
            <Typography color="text.secondary">{t('idCard.loadingHistory')}</Typography>
          ) : history.rows.length === 0 ? (
            <Typography color="text.secondary">{t('idCard.noHistory')}</Typography>
          ) : (
            <Box>
              <Typography variant="subtitle1" gutterBottom>{t('idCard.history.recordCount', { count: history.count })}</Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th align="left">{t('idCard.history.issuedAt')}</th>
                      <th align="left">{t('idCard.history.user')}</th>
                      <th align="left">{t('idCard.history.cardNo')}</th>
                      <th align="left">{t('idCard.history.template')}</th>
                      <th align="left">{t('idCard.history.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.rows.map((r, idx) => (
                      <tr key={idx}>
                        <td>{new Date(r.issuedAt || r.createdAt).toLocaleString()}</td>
                        <td>{r.User?.name || r.userId}</td>
                        <td>{r.cardNo}</td>
                        <td>{r.template?.name || r.templateId}</td>
                        <td>{r.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Paper>
            </Box>
          )}
        </TabPanel>
      </Paper>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
      <Dialog open={userPickerOpen} onClose={() => setUserPickerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('idCard.selectUserForPreview')}</DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField fullWidth size="small" label={t('idCard.dialog.searchByNameOrEmail')} value={userSearch} onChange={(e) => setUserSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') searchUsers(userSearch); }} />
            <Button variant="contained" onClick={() => searchUsers(userSearch)} disabled={userLoading}>{t('idCard.dialog.search')}</Button>
          </Stack>
          <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th align="left" style={{ padding: 8 }}>{t('idCard.dialog.name')}</th>
                  <th align="left" style={{ padding: 8 }}>{t('idCard.dialog.email')}</th>
                  <th align="left" style={{ padding: 8 }}>{t('idCard.dialog.role')}</th>
                  <th align="left" style={{ padding: 8 }}>{t('idCard.dialog.action')}</th>
                </tr>
              </thead>
              <tbody>
                {userResults.map(u => (
                  <tr key={u.id}>
                    <td style={{ padding: 8 }}>{u.name}</td>
                    <td style={{ padding: 8 }}>{u.email}</td>
                    <td style={{ padding: 8 }}>{u.role || u.Role?.name || ''}</td>
                    <td style={{ padding: 8 }}>
                      <Button size="small" variant="outlined" onClick={() => handleChooseUser(u.id)}>{t('idCard.dialog.preview')}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserPickerOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={quickPreviewOpen} onClose={() => setQuickPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('idCard.dialog.templatePreview')}</DialogTitle>
        <DialogContent dividers>
          {!quickPreviewData ? (
            <Typography color="text.secondary">{t('idCard.dialog.generatingPreview')}</Typography>
          ) : (
            <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
              {renderIdCardPreview(quickPreviewData)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickPreviewOpen(false)}>{t('common.close')}</Button>
          <Button onClick={() => { setQuickPreviewOpen(false); setPreviewData(quickPreviewData); setTab(3); }} variant="contained">{t('idCard.dialog.openInPreview')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default function IDCardWorkshopPage() {
  // ID Card Workshop is available in all school phases (nursery/primary and secondary).
  return <IDCardWorkshopInner />;
}
